@file:Suppress("MagicNumber", "TooGenericExceptionCaught")

package com.nonopn.skydome.common.app.pages.account

import com.nonopn.dispatchers.DefaultDispatcher
import com.nonopn.skydome.common.app.api.impl.AccountCheckoutRequest
import com.nonopn.skydome.common.app.api.impl.AccountStatus
import com.nonopn.skydome.common.app.api.impl.ProbeApp
import com.nonopn.skydome.common.app.api.impl.UpdateProbe
import com.nonopn.skydome.common.app.api.model.DashboardImpactReport
import com.nonopn.skydome.common.app.detection.ThunderDetection
import com.nonopn.skydome.common.app.home.AppModel
import com.nonopn.skydome.common.app.pages.main.model.api.Probe
import com.nonopn.skydome.common.app.pages.main.model.api.Project
import com.nonopn.skydome.common.app.pages.probes.AccountActions
import com.nonopn.skydome.common.app.pages.probes.ProbeAction
import com.nonopn.skydome.common.app.pages.probes.ProbeCall
import com.nonopn.skydome.common.app.pages.probes.ProbeCallError
import com.nonopn.skydome.common.app.pages.probes.ProbeFailure
import com.nonopn.skydome.common.app.pages.probes.ProbeStatusInput
import com.nonopn.skydome.common.app.pages.probes.isPaidBySubscription
import com.nonopn.skydome.common.app.pages.probes.isProbeDeleted
import com.nonopn.skydome.common.app.pages.probes.isUsableStatus
import com.nonopn.skydome.common.app.pages.probes.nowSecondsUtc
import com.nonopn.skydome.common.app.pages.probes.accountActions
import com.nonopn.skydome.common.app.pages.probes.loadAccountStatus
import com.nonopn.skydome.common.app.pages.probes.probeActionFor
import com.nonopn.skydome.common.app.pages.probes.visibleProbes
import com.nonopn.skydome.common.app.pages.probes.resolveProbeStatus
import com.nonopn.skydome.common.app.pages.probes.toProbeCallError
import androidx.compose.ui.platform.UriHandler
import com.nonopn.skydome.common.app.pages.promocodes.PromoCheckResult
import com.nonopn.skydome.common.app.pages.promocodes.PromoCodeUiState
import com.nonopn.skydome.common.app.pages.promocodes.canApplyPromoCode
import com.nonopn.skydome.common.app.pages.promocodes.checkPromoCode
import com.nonopn.skydome.common.app.pages.promocodes.promoCheckoutFailure
import com.nonopn.skydome.common.app.pages.promocodes.promoSubscriptionRunning
import com.nonopn.skywatcher.app.utils.safeLaunch

import eu.codlab.viewmodel.StateViewModel

import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.SupervisorJob
import kotlinx.coroutines.async

import kotlin.math.PI
import kotlin.math.asin
import kotlin.math.cos
import kotlin.math.pow
import kotlin.math.sin
import kotlin.math.sqrt

// ──────────────────────────────────────────────
// State
// ──────────────────────────────────────────────

data class AccountState(
    val isLoading: Boolean = true,
    val probes: List<Probe> = emptyList(),
    val lpsProjects: List<Project> = emptyList(),
    val totalImpacts: Int = 0,
    val error: String? = null,
    // Rename-specific error: shown as non-destructive banner, does not blank the screen
    val renameError: String? = null,
    // Probe selected on the map (null = show all, centered on bounding box)
    val selectedProbeUuid: String? = null,
    // Probe route error (checkout of an unpaid / trial probe) and probe whose call is in progress
    val probeCallError: ProbeCallError? = null,
    val busyProbeUuid: String? = null,
    // What the account can do according to GET /api/account/status (null = route failed: previous behaviour)
    val actions: AccountActions? = null,
    // "Promo code" field shown before the payment of unpaid / trial probes, and whether a subscription is running
    val promo: PromoCodeUiState = PromoCodeUiState(),
    val subscriptionRunning: Boolean? = null,
)

// ──────────────────────────────────────────────
// ViewModel
// ──────────────────────────────────────────────

class AccountModel(
    private val appModel: AppModel
) : StateViewModel<AccountState>(AccountState()) {
    private val backgroundScope = CoroutineScope(DefaultDispatcher + SupervisorJob())

    init {
        load()
    }

    fun load() = safeLaunch {
        backgroundScope.async {
            updateState { copy(isLoading = true, error = null) }
            try {
                val api = appModel.states.value.currentApi
                val status = appModel.loadAccountStatus()
                val reports = try {
                    appModel.cachedDashboardReports()
                } catch (e: Exception) {
                    // The status route is enough to list the probes; impacts are then simply unknown
                    if (status == null) throw e
                    emptyList()
                }

                val nowSeconds = nowSecondsUtc()
                val probes = if (status != null) {
                    probesFromStatus(status, nowSeconds)
                } else {
                    probesFromReports(reports, nowSeconds)
                }
                val totalImpacts = reports.sumOf { report ->
                    report.periods[DashboardImpactReport.DashboardImpactPeriod.Year]?.total ?: 0
                }

                // Load LPS Manager projects if API is available
                val projects = try {
                    api?.estimatesData()?.map { it.project } ?: emptyList()
                } catch (_: Exception) {
                    emptyList()
                }

                updateState {
                    copy(
                        isLoading = false,
                        probes = probes,
                        lpsProjects = projects,
                        totalImpacts = totalImpacts,
                        error = null,
                        actions = status?.accountActions(),
                        subscriptionRunning = status.promoSubscriptionRunning(),
                    )
                }
            } catch (e: Exception) {
                e.printStackTrace()
                updateState { copy(isLoading = false, error = e.message) }
            }
        }
    }

    // Probes of the account according to the status route (statuses, source, trial end of the server)
    private fun probesFromStatus(status: AccountStatus, nowSeconds: Long): List<Probe> =
        status.visibleProbes(nowSeconds).map { (probe, input) ->
            Probe(
                uuid = probe.uuid,
                name = probe.name,
                latitude = probe.latitude,
                longitude = probe.longitude,
                radiusKm = probe.radiusKm.takeIf { it > 0 } ?: 100,
                enabled = isUsableStatus(resolveProbeStatus(input, nowSeconds)),
                isMain = false,
                isSkysentinel = false,
                isVirtual = false,
                createdAtSecondsUtc = probe.createdAtSecondsUtc,
                status = input.status,
                isDemo = input.isDemo,
                validUntilSecondsUtc = input.validUntilSecondsUtc,
                trialEndsAtSecondsUtc = input.trialEndsAtSecondsUtc,
            )
        }

    // Fallback (status route failed): probes of the dashboard reports, as before
    private fun probesFromReports(
        reports: List<DashboardImpactReport>,
        nowSeconds: Long
    ): List<Probe> = reports.filterNot { isProbeDeleted(it.probe.status) }.map { report ->
        val input = report.probe.statusInput()
        Probe(
            uuid = report.probe.uuid,
            name = report.probe.name,
            latitude = report.probe.latitude,
            longitude = report.probe.longitude,
            radiusKm = 100,
            // Without status from the server, probes stay active (previous behaviour)
            enabled = isUsableStatus(resolveProbeStatus(input, nowSeconds)),
            isMain = false,
            isSkysentinel = false,
            isVirtual = false,
            createdAtSecondsUtc = report.probe.createdAtSecondsUtc,
            status = report.probe.status,
            isDemo = report.probe.isDemo,
            validUntilSecondsUtc = report.probe.validUntilSecondsUtc,
            trialEndsAtSecondsUtc = report.probe.trialEndsAtSecondsUtc,
        )
    }

    fun confirmRename(probeUuid: String, newName: String) = safeLaunch {
        val trimmed = newName.trim()
        if (trimmed.isBlank()) return@safeLaunch
        try {
            val api = appModel.states.value.currentApi ?: return@safeLaunch
            val probe = states.value.probes.find { it.uuid == probeUuid } ?: return@safeLaunch
            val thunder = ThunderDetection(
                uuid = probeUuid,
                name = probe.name,
                latitude = probe.latitude,
                longitude = probe.longitude,
            )
            api.updateProbe(thunder, UpdateProbe(name = trimmed))
            updateState {
                copy(
                    probes = probes.map { if (it.uuid == probeUuid) it.copy(name = trimmed) else it },
                )
            }
        } catch (e: Exception) {
            e.printStackTrace()
            // Use renameError so the screen content stays visible
            updateState { copy(renameError = e.message) }
        }
    }

    fun clearRenameError() = updateState { copy(renameError = null) }

    fun selectProbe(uuid: String?) = updateState { copy(selectedProbeUuid = uuid) }

    fun clearProbeCallError() = updateState { copy(probeCallError = null) }

    // ─── Promo code ──────────────────────────────────────────────────────────

    fun onPromoInput(text: String) = updateState { copy(promo = promo.typing(text)) }

    fun removePromo() = updateState { copy(promo = promo.removed()) }

    /** "Apply": GET /api/checkout/promotion-codes/{code} with the bearer of the account. */
    fun applyPromo() {
        val input = states.value.promo.input
        if (!canApplyPromoCode(input, states.value.promo.checking)) return
        updateState { copy(promo = promo.startChecking()) }
        safeLaunch {
            when (val result = appModel.checkPromoCode(input)) {
                is PromoCheckResult.Accepted -> updateState { copy(promo = promo.accepted(result.code)) }
                is PromoCheckResult.Refused -> updateState { copy(promo = promo.refused(result.failure)) }
            }
        }
    }

    // "Pay for my probe" from the error dialog (trial unavailable): converts the account demo into a paid probe
    fun payExistingProbe(uriHandler: UriHandler) {
        clearProbeCallError()
        runCheckout(ProbeCall.Upgrade, null, uriHandler)
    }

    // Probe card button: "Upgrade to a paid probe" (trial) -> checkout/upgrade,
    // "Complete payment" (unpaid probes) -> checkout/account
    fun payProbe(probeUuid: String, input: ProbeStatusInput, uriHandler: UriHandler) {
        when (probeActionFor(input, resolveProbeStatus(input, nowSecondsUtc()), states.value.actions)) {
            ProbeAction.Upgrade -> runCheckout(ProbeCall.Upgrade, probeUuid, uriHandler)
            ProbeAction.FinalizePayment -> runCheckout(ProbeCall.Account, probeUuid, uriHandler)
            ProbeAction.None -> Unit
        }
    }

    private fun runCheckout(call: ProbeCall, probeUuid: String?, uriHandler: UriHandler) = safeLaunch {
        val providers = appModel.states.value.apiProvider
        val api = providers?.apiNormal ?: providers?.apiAdmin ?: return@safeLaunch
        updateState { copy(busyProbeUuid = probeUuid, probeCallError = null) }
        try {
            // The applied promo code (if any) goes with the checkout of the account or of the demo upgrade
            val request = AccountCheckoutRequest(ProbeApp.STRIKE_RADAR, promotionCode = states.value.promo.codeToSend)
            val answer = if (call == ProbeCall.Upgrade) api.checkoutUpgrade(request) else api.checkoutAccount(request)
            appModel.invalidateDashboardCache()
            val link = answer.paymentLink
            updateState { copy(busyProbeUuid = null) }
            if (isPaidBySubscription(link)) {
                // Paid by the active subscription: tell the user and refresh the probes
                updateState { copy(probeCallError = ProbeCallError(ProbeFailure.PaidBySubscription)) }
                load()
            } else {
                uriHandler.openUri(link.orEmpty())
            }
        } catch (e: Throwable) {
            // A refused promo code is shown under its field, the payment stays possible without it
            val promoFailure = e.promoCheckoutFailure()
            if (promoFailure != null) {
                updateState { copy(busyProbeUuid = null, promo = promo.refused(promoFailure)) }
            } else {
                val error = e.toProbeCallError(call)
                updateState { copy(busyProbeUuid = null, probeCallError = error) }
            }
        }
    }

    companion object {
        // Haversine distance in km between two WGS84 points
        fun haversineKm(lat1: Double, lon1: Double, lat2: Double, lon2: Double): Double {
            val r = 6371.0
            val dLat = (lat2 - lat1) * PI / 180.0
            val dLon = (lon2 - lon1) * PI / 180.0
            val a = sin(dLat / 2).pow(2) +
                cos(lat1 * PI / 180.0) * cos(lat2 * PI / 180.0) * sin(dLon / 2).pow(2)
            return 2 * r * asin(sqrt(a))
        }
    }
}
